import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Plus,
  Trash2,
  Edit,
  Loader2,
  AlertCircle,
  Users,
  BarChart3,
} from 'lucide-react';
import {
  GlassCard,
  GlassButton,
  GlassBadge,
  ProgressBar,
  SectionReveal,
} from '@/design-system';
import type { RootState, AppDispatch } from '@/store';
import type { Feature } from '@/types';
import {
  fetchFeatureAllocations,
  deleteFeatureAllocation,
  type FeatureAllocation,
} from '@/store/slices/featureAllocationsSlice';
import FeatureAllocationForm from './FeatureAllocationForm';

interface FeatureAllocationsTabProps {
  feature: Feature;
}

const FeatureAllocationsTab: React.FC<FeatureAllocationsTabProps> = ({ feature }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAllocation, setEditingAllocation] = useState<FeatureAllocation | null>(null);

  // Selectors
  const allocations = useSelector(
    (state: RootState) =>
      state.featureAllocations.allocationsByFeatureId[feature.id] || []
  );
  const isLoading = useSelector((state: RootState) =>
    state.featureAllocations.loadingFeatureIds.includes(feature.id)
  );
  const deletingIds = useSelector(
    (state: RootState) => state.featureAllocations.deletingAllocationIds
  );
  const error = useSelector((state: RootState) => state.featureAllocations.error);

  // Fetch allocations on mount
  useEffect(() => {
    if (feature.id) {
      dispatch(fetchFeatureAllocations(feature.id));
    }
  }, [dispatch, feature.id]);

  // Calculate totals
  const totalAllocated = allocations.reduce((sum, a) => sum + a.teamWeeks, 0);
  const estimatedTeamWeeks = feature.estimatedTeamWeeks || 0;
  const allocationPercentage = estimatedTeamWeeks > 0
    ? Math.round((totalAllocated / estimatedTeamWeeks) * 100)
    : 0;

  // Handle delete
  const handleDelete = useCallback(
    async (allocation: FeatureAllocation) => {
      if (window.confirm('Are you sure you want to delete this allocation?')) {
        await dispatch(deleteFeatureAllocation({ id: allocation.id, featureId: feature.id }));
      }
    },
    [dispatch, feature.id]
  );

  // Handle edit
  const handleEdit = useCallback((allocation: FeatureAllocation) => {
    setEditingAllocation(allocation);
    setIsFormOpen(true);
  }, []);

  // Handle form close
  const handleFormClose = useCallback(() => {
    setIsFormOpen(false);
    setEditingAllocation(null);
  }, []);

  // Handle form success
  const handleFormSuccess = useCallback(() => {
    handleFormClose();
    dispatch(fetchFeatureAllocations(feature.id));
  }, [dispatch, feature.id, handleFormClose]);

  // Loading state
  if (isLoading && allocations.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-accent-cyan animate-spin" />
        <span className="ml-3 text-muted-foreground">Loading allocations...</span>
      </div>
    );
  }

  // Error state
  if (error && allocations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="w-10 h-10 text-accent-rose mb-3" />
        <p className="text-muted-foreground mb-4">{error}</p>
        <GlassButton
          variant="secondary"
          size="sm"
          onClick={() => dispatch(fetchFeatureAllocations(feature.id))}
        >
          Retry
        </GlassButton>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <SectionReveal>
        <GlassCard variant="strong" padding="md">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-accent-blue" />
              <h3 className="text-lg font-medium text-foreground">Capacity Allocation Summary</h3>
            </div>
            <GlassButton
              variant="primary"
              size="sm"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => setIsFormOpen(true)}
            >
              Add Allocation
            </GlassButton>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-accent-blue">{estimatedTeamWeeks}</div>
              <div className="text-xs text-muted-foreground/60">Estimated TWs</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-accent-emerald">{totalAllocated}</div>
              <div className="text-xs text-muted-foreground/60">Allocated TWs</div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${
                allocationPercentage >= 100 ? 'text-accent-emerald' :
                allocationPercentage >= 75 ? 'text-accent-amber' : 'text-accent-rose'
              }`}>
                {allocationPercentage}%
              </div>
              <div className="text-xs text-muted-foreground/60">Allocated</div>
            </div>
          </div>

          <ProgressBar
            value={Math.min(allocationPercentage, 100)}
            color={
              allocationPercentage >= 100 ? 'emerald' :
              allocationPercentage >= 75 ? 'amber' : 'rose'
            }
            size="md"
            gradient
          />
        </GlassCard>
      </SectionReveal>

      {/* Allocations List */}
      <SectionReveal delay={0.1}>
        {allocations.length === 0 ? (
          <GlassCard variant="subtle" padding="lg">
            <div className="text-center py-8">
              <div className="p-3 rounded-xl bg-gradient-to-br from-accent-blue/10 to-accent-cyan/10 w-fit mx-auto mb-4">
                <Users className="w-10 h-10 text-accent-blue" />
              </div>
              <h3 className="text-base font-medium text-foreground mb-2">No Allocations Yet</h3>
              <p className="text-sm text-muted-foreground/60 mb-4">
                Add capacity allocations to track which programs are contributing to this feature.
              </p>
              <GlassButton
                variant="primary"
                size="sm"
                leftIcon={<Plus className="w-4 h-4" />}
                onClick={() => setIsFormOpen(true)}
              >
                Add First Allocation
              </GlassButton>
            </div>
          </GlassCard>
        ) : (
          <div className="space-y-3">
            {allocations.map((allocation) => {
              const isDeleting = deletingIds.includes(allocation.id);

              return (
                <GlassCard
                  key={allocation.id}
                  variant="default"
                  padding="sm"
                  hover
                  className="group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="font-medium text-foreground">
                          {allocation.program?.name || 'Unknown Program'}
                        </div>
                        {allocation.team && (
                          <div className="text-sm text-muted-foreground/60">
                            Team: {allocation.team.name}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-lg font-bold text-accent-blue">
                          {allocation.teamWeeks} TWs
                        </div>
                        {allocation.scenario && (
                          <GlassBadge variant="info" size="sm">
                            {allocation.scenario.name}
                          </GlassBadge>
                        )}
                      </div>

                      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleEdit(allocation)}
                          className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                          title="Edit"
                          aria-label="Edit allocation"
                        >
                          <Edit className="w-4 h-4 text-muted-foreground hover:text-accent-blue" />
                        </button>
                        <button
                          onClick={() => handleDelete(allocation)}
                          disabled={isDeleting}
                          className="p-1.5 rounded-lg hover:bg-white/10 transition-colors disabled:opacity-50"
                          title="Delete"
                          aria-label="Delete allocation"
                        >
                          {isDeleting ? (
                            <Loader2 className="w-4 h-4 text-muted-foreground/60 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4 text-muted-foreground hover:text-accent-rose" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </GlassCard>
              );
            })}
          </div>
        )}
      </SectionReveal>

      {/* Allocation Form Modal */}
      {isFormOpen && (
        <FeatureAllocationForm
          featureId={feature.id}
          allocation={editingAllocation}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

export default FeatureAllocationsTab;
